import type { AxiosResponse } from 'axios';
import axios from 'axios';
import FileSaver from 'file-saver';

export function defaultGetNameFromResponse(blob: AxiosResponse) {
  const contentDisposition = blob.headers['content-disposition'] as string;

  return (
    contentDisposition?.match(/filename=(?:\\?")?([^"]+)/i)?.[1] ?? 'noFileName'
  );
}

const secondParameterIsTDownloadUrlOptions = (
  param:
    | string
    | ((blob: AxiosResponse) => string)
    | TDownloadUrlOptions = defaultGetNameFromResponse,
): param is TDownloadUrlOptions => {
  return !!(param as TDownloadUrlOptions).postData;
};

export type TDownloadUrlOptions = {
  fileName?: string;
  getNameFromResponse?: boolean | ((blob: AxiosResponse) => string);
  postData: string;
};
/**
 * This function gets an URL and a file name and performs a file download.
 *
 * @param url The url to fetch
 * @param getNameFromResponse The name will be given by the 'content-disposition' prop in the headers
 */
export async function downloadUrl(
  url: string,
  getNameFromResponse?: (blob: AxiosResponse) => string,
): Promise<void>;
/**
 * This function gets an URL and a file name and performs a file download.
 *
 * @param url The url to fetch
 * @param fileName The name the file will have as default in the save dialog
 */
export async function downloadUrl(
  url: string,
  fileName: string | TDownloadUrlOptions,
): Promise<void>;

export async function downloadUrl(
  url: string,
  secondParameter:
    | string
    | ((blob: AxiosResponse) => string)
    | TDownloadUrlOptions = defaultGetNameFromResponse,
): Promise<void> {
  if (secondParameterIsTDownloadUrlOptions(secondParameter)) {
    const blob = await axios.post(
      url,
      {
        postData: secondParameter.postData,
      },
      { responseType: 'blob' },
    );

    const getNameFromResponse =
      secondParameter.getNameFromResponse instanceof Function
        ? secondParameter.getNameFromResponse
        : secondParameter.getNameFromResponse === true
          ? defaultGetNameFromResponse
          : () => 'noname';

    const actualFileName =
      secondParameter.fileName ?? getNameFromResponse(blob);

    FileSaver.saveAs(new Blob([blob.data]), actualFileName);
  } else {
    const blob = await axios.get(url, {
      responseType: 'blob',
    });

    const actualFileName =
      typeof secondParameter === 'string'
        ? secondParameter
        : secondParameter(blob);

    FileSaver.saveAs(new Blob([blob.data]), actualFileName);
  }
}

export async function downloadUrlWithoutCaching(
  downloadUrl: string,
  fileName?: string,
) {
  const link = document.createElement('a');
  link.href = downloadUrl;

  link.setAttribute('download', fileName ?? '');

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
